// 報獎（大獎慶祝）Spine 骨骼載入與工廠 — 沿用 Unity 來源 Art/Animaton 的 Spine 4.2.43 資產。
// 對應 Unity RewardPresentationUI 家族三支動態骨架（GUID 已於來源 prefab 核對）：
//   - ani_win（3 頁）：逐轉大獎報獎 WinCelebrationUI。動畫 {big|mega|super|epic}Win{1|2}_(in|loop)，
//     變體 1=主遊戲、2=免費遊戲；序列固定 _in（播一次）→ _loop（循環）。門檻 10/25/50/100×bet。
//   - ani_cutscene（2 頁）：雙用途。(a) 免費遊戲進場字卡 FreeSpinsUI（LandscapeUI.prefab 覆寫
//     SkeletonData 為本骨架）：cutscene_in_L → cutscene_loop_L（另有 _P 直版與未用的 cutscene_out_*）。
//     (b) 免費遊戲整輪結算 FreeGameSettleUI：totalWin{0-4}_(in|loop)，依整輪累積 Scatter 數
//     選 totalWin1~4（index=clamp(scatter/2,0,3)）。
//   - ani_maxWin（1 頁）：最高贏分 ShowMaxWin。骨架動畫命名不規則（in / MaxWin…），改於 runtime 偵測。
// 骨骼為二進位 .skel、圖集 .atlas（pma:true 預乘 alpha），已由 Unity .skel.bytes/.atlas.txt 去後綴複製至 public/spine/。

import { Assets } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi-v8'
import { isSpinePrimed, primeSpineAssets } from '../../framework/assets/spineBundle'

const BASE = '/spine'

interface RewardSpineDef {
  /** 骨骼 .skel 路徑（同時作為 Assets alias）。 */
  readonly skel: string
  /** 圖集 .atlas 路徑（同時作為 Assets alias；atlas 內引用的 png 由 spine-pixi 自動載入）。 */
  readonly atlas: string
}

const WIN: RewardSpineDef = { skel: `${BASE}/win/ani_win.skel`, atlas: `${BASE}/win/ani_win.atlas` }
const CUTSCENE: RewardSpineDef = { skel: `${BASE}/cutscene/ani_cutscene.skel`, atlas: `${BASE}/cutscene/ani_cutscene.atlas` }
const MAXWIN: RewardSpineDef = { skel: `${BASE}/maxWin/ani_maxWin.skel`, atlas: `${BASE}/maxWin/ani_maxWin.atlas` }

let job: Promise<void> | null = null

/**
 * 載入三支報獎骨骼 + 圖集（約 10MB）。**不在首屏**：由 main.ts 於 loading 淡出後背景載入，
 * RewardOverlayView 於每次報獎前 `await loadRewardSpines()`（可重複呼叫，同一份任務）。
 * 以 allSettled 個別載入：即使某一組（如 maxWin）資產有問題，也不擋住報獎核心 ani_win。
 */
export function loadRewardSpines(): Promise<void> {
  job ??= loadAll()
  return job
}

async function loadAll(): Promise<void> {
  const groups = [WIN, CUTSCENE, MAXWIN]
  await primeSpineAssets(groups)
  const missing = groups.filter((g) => !isSpinePrimed(g))
  for (const g of missing) {
    Assets.add({ alias: g.skel, src: g.skel })
    Assets.add({ alias: g.atlas, src: g.atlas })
  }
  await Promise.allSettled(missing.map((g) => Assets.load([g.skel, g.atlas])))
}

/** 建立大獎慶祝 Spine（ani_win）。載入失敗回 null，讓呼叫端退回自繪字卡。 */
export function createWinCelebrationSpine(): Spine | null {
  return tryCreate(WIN)
}

/** 建立免費遊戲結算 Spine（ani_cutscene）。載入失敗回 null。 */
export function createCutsceneSpine(): Spine | null {
  return tryCreate(CUTSCENE)
}

/** 建立最高贏分 Spine（ani_maxWin）。載入失敗回 null。 */
export function createMaxWinSpine(): Spine | null {
  return tryCreate(MAXWIN)
}

function tryCreate(def: RewardSpineDef): Spine | null {
  try {
    return Spine.from({ skeleton: def.skel, atlas: def.atlas })
  } catch {
    return null
  }
}

/**
 * 從骨架的動畫清單偵測「進場 → 循環」兩支動畫名（供命名不規則的 ani_maxWin 使用）。
 * 進場優先取結尾為 _in 或名為 in 者、其次含 in、再退第一支；循環優先取含 loop、其次任一非進場者。
 */
export function pickIntroLoop(spine: Spine): { readonly intro: string; readonly loop: string } | null {
  const names = spine.skeleton.data.animations.map((a) => a.name)
  if (names.length === 0) return null
  const intro =
    names.find((n) => /(^|_)in$/i.test(n)) ?? names.find((n) => /in/i.test(n)) ?? names[0]!
  const loop = names.find((n) => /loop/i.test(n)) ?? names.find((n) => n !== intro) ?? intro
  return { intro, loop }
}
